import { Skeleton } from "@/components/ui/skeleton";

/**
 * Standard skeleton grid used across Dashboard, Leaderboard, Community, Terminal etc.
 * Renders N placeholder rows/cards using the shadcn Skeleton primitive.
 *
 * Variants:
 *   - "cards"      → 3-up card grid (great for stat/metric cards)
 *   - "rows"       → vertical rows (leaderboard, community posts)
 *   - "watchlist"  → symbol + price + change columns (Terminal watchlist)
 */
export default function SkeletonGrid({ variant = "cards", count = 4, cols }) {
    if (variant === "cards") {
        const gridCls = cols
            ? `grid gap-4 ${cols === 3 ? "grid-cols-1 md:grid-cols-3" : cols === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-2 md:grid-cols-4"}`
            : "grid grid-cols-2 md:grid-cols-4 gap-4";
        return (
            <div className={gridCls} data-testid="skeleton-cards">
                {Array.from({ length: count }).map((_, i) => (
                    <div key={`sk-card-${i}`} className="border border-border bg-card rounded p-4 space-y-3">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-8 w-32" />
                        <Skeleton className="h-3 w-24" />
                    </div>
                ))}
            </div>
        );
    }
    if (variant === "rows") {
        return (
            <div className="space-y-2" data-testid="skeleton-rows">
                {Array.from({ length: count }).map((_, i) => (
                    <div key={`sk-row-${i}`} className="border border-border bg-card rounded p-4 flex items-center gap-4">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-40" />
                            <Skeleton className="h-3 w-64" />
                        </div>
                        <Skeleton className="h-6 w-16" />
                    </div>
                ))}
            </div>
        );
    }
    if (variant === "watchlist") {
        return (
            <div className="space-y-1" data-testid="skeleton-watchlist">
                {Array.from({ length: count }).map((_, i) => (
                    <div key={`sk-wl-${i}`} className="flex items-center justify-between px-2 py-2 border-b border-border/50">
                        <div className="space-y-1.5">
                            <Skeleton className="h-4 w-16" />
                            <Skeleton className="h-2.5 w-24" />
                        </div>
                        <div className="text-right space-y-1.5">
                            <Skeleton className="h-4 w-16 ml-auto" />
                            <Skeleton className="h-2.5 w-10 ml-auto" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    return null;
}
